import { connect as formikConnect } from 'formik';
import snakeCase from 'lodash/snakeCase';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompact';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { segmentTrack } from 'src/app/state/actions';
import type { Dispatch } from 'src/app/state/store';

import Renderer from '.';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchFieldFocused: ({ target }) => {
    const { name } = target;
    const payload = {
      pagepath: window.location.pathname,
      fieldname: snakeCase(name),
    };
    return dispatch(segmentTrack(UNIFIED.fieldFocused, payload));
  },
});

const mapStateToProps = ({ client }) => ({
  client,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ZipCodeInput = compose(
  formikConnect,
  connector,
  withHandlers({
    mapPropsToStatus: () => ({
      isSubmitting: false,
      isZipCodeDeliverable: null,
    }),
    handleChange: props => e => {
      const { name, formik, client, setIsZipCodeDeliverable } = props;
      const { values, setFieldValue, setStatus } = formik;

      // Safari and Firefox do not support the number input type, and so, until we create a number input component,
      // this handler blocks non-numeric inputs.
      if (isNaN(e.target.value)) {
        return;
      }

      // This logic is copy-pasted into `setRelatedFields` value in the DeliveryAddress AutoComplete component
      const checkZipCode = async zipCode => {
        if (zipCode.length === 5) {
          setStatus({ submitting: true });
          try {
            const resp = await client.checkZipCodeEligibility(zipCode);
            const isDeliverable = resp.data?.isDeliverable;
            setStatus({ submitting: false, isZipCodeDeliverable: isDeliverable });
            setIsZipCodeDeliverable?.(isDeliverable);
            setFieldValue(`${name}Deliverable`, isDeliverable);
          } catch (e) {
            setFieldValue(`${name}Deliverable`, undefined);
            setStatus({ submitting: false, isZipCodeDeliverable: null });
          }
        } else {
          setStatus({ submitting: false, isZipCodeDeliverable: null });
          setFieldValue(`${name}Deliverable`, undefined);
        }
      };

      const newZipCode = e.target.value;

      let zipCode = values[name];
      if (newZipCode.length <= 5) {
        zipCode = newZipCode;
      }

      setFieldValue(name, zipCode);
      checkZipCode(zipCode);
    },
  })
)(Renderer);

export default ZipCodeInput;
